// Growth-spurt chooser — the VS-style level-up moment. When the spurt meter
// fills (game/growth-spurt.ts), main.ts opens this overlay; the frame loop
// freezes on isOpen() exactly like the skill shop. The choice is mandatory:
// no close button, ESC is ignored. Cards offer mutations (MUTATION_DEFS copy)
// or power levels (POWER_DEFS copy) — see amoeba-powers.md.

import type { Entity } from '../game/components'
import type { EventBus } from '../game/events'
import { MUTATION_DEFS } from '../game/mutations'
import { applyMutationStack } from '../game/mutations'
import { POWER_DEFS, grantPowerLevel, powerLevel } from '../game/powers'
import { consumeSpurt, sampleSpurtChoices, spurtPending, type SpurtChoice } from '../game/growth-spurt'
import { triggerNarrator } from '../engine/narrator'
import { playSfx } from '../engine/audio'
import { T } from '../game/tunables'
import { skillIconSvg } from './skill-icons'
import { MUTATION_GLYPH } from './mutation-tray'

export interface GrowthSpurtChooser {
  isOpen(): boolean
  /** Open the overlay if the player has a spurt pending. */
  maybeOpen(player: Entity): void
}

function tintToRgba([r, g, b]: [number, number, number], alpha: number): string {
  return `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${alpha})`
}

export function createGrowthSpurtChooser(events: EventBus): GrowthSpurtChooser {
  const overlay = document.getElementById('growth-spurt') as HTMLDivElement | null
  const cardRow = document.getElementById('growth-spurt-cards') as HTMLDivElement | null
  let open = false

  function close(): void {
    open = false
    overlay?.classList.remove('visible')
  }

  function makeCard(opts: {
    sign: string
    tint: [number, number, number]
    name: string
    flavor: string
    /** SVG markup (powers) or a unicode glyph (mutations). */
    iconHtml?: string
    iconText?: string
    description?: string
    stackLine?: string
    onPick: () => void
  }): HTMLDivElement {
    const card = document.createElement('div')
    card.className = 'spurt-card'
    card.dataset.sign = opts.sign
    card.style.background = tintToRgba(opts.tint, 0.10)
    card.setAttribute('data-focus', '')
    card.tabIndex = -1

    const head = document.createElement('div')
    head.className = 'spurt-card-head'
    if (opts.iconHtml || opts.iconText) {
      const icon = document.createElement('span')
      icon.className = 'spurt-card-icon'
      if (opts.iconHtml) icon.innerHTML = opts.iconHtml
      else icon.textContent = opts.iconText!
      head.appendChild(icon)
    }
    const name = document.createElement('div')
    name.className = 'spurt-card-name'
    name.textContent = opts.name
    head.appendChild(name)

    const flavor = document.createElement('div')
    flavor.className = 'spurt-card-flavor'
    flavor.textContent = opts.flavor

    card.appendChild(head)
    card.appendChild(flavor)

    if (opts.description) {
      const descEl = document.createElement('div')
      descEl.className = 'spurt-card-desc'
      descEl.textContent = opts.description
      card.appendChild(descEl)
    }

    if (opts.stackLine) {
      const stackEl = document.createElement('div')
      stackEl.className = 'spurt-card-stack'
      stackEl.textContent = opts.stackLine
      card.appendChild(stackEl)
    }

    card.addEventListener('click', () => {
      opts.onPick()
      playSfx('orb_collect', { volume: 0.3 })
      close()
    })
    return card
  }

  function render(player: Entity, choices: SpurtChoice[]): void {
    if (!cardRow) return
    cardRow.innerHTML = ''
    for (const choice of choices) {
      if (choice.type === 'mutation') {
        const def = MUTATION_DEFS[choice.kind]
        const stacks = player.mutations?.stacks[choice.kind] ?? 0
        cardRow.appendChild(makeCard({
          sign: def.sign,
          tint: def.tint,
          name: def.name,
          flavor: def.flavor,
          iconText: MUTATION_GLYPH[choice.kind],
          stackLine: stacks > 0 ? `${stacks + 1} of ${T.mutations.maxStacks}` : undefined,
          onPick: () => {
            const result = applyMutationStack(player, choice.kind)
            if (result.applied) {
              events.emit({ type: 'mutation.applied', player, kind: choice.kind, firstTime: result.isFirstThisRun })
            }
            consumeSpurt(player)
          },
        }))
      } else {
        const def = POWER_DEFS[choice.id]
        const level = powerLevel(player, choice.id)
        cardRow.appendChild(makeCard({
          sign: 'power',
          tint: def.tint,
          name: def.name,
          flavor: def.flavor,
          iconHtml: skillIconSvg(choice.id),
          description: def.description,
          stackLine: level > 0 ? `L${level} → L${level + 1}` : `new · L1 of ${def.maxLevel}`,
          onPick: () => {
            grantPowerLevel(player, choice.id)
            consumeSpurt(player)
          },
        }))
      }
    }
  }

  return {
    isOpen: () => open,
    maybeOpen(player) {
      if (open || !spurtPending(player)) return
      const choices = sampleSpurtChoices(player)
      if (choices.length === 0) {
        // Everything is at max — the meter still advances quietly.
        consumeSpurt(player)
        return
      }
      render(player, choices)
      open = true
      overlay?.classList.add('visible')
      playSfx('engulf_swell', { volume: 0.25 })
      triggerNarrator('first_growth_spurt')
    },
  }
}
